/**
 * nodes/NodeDeliveryRow.tsx —— 节点交付行（U-S3 缺件建件）
 * 真源（102，逐字读）：`components/nodes/NodeDeliveryRow.vue`（283 行）——抄形态/尺寸/交互，Vue→React+TS。
 * 依据：《动作生命周期语义-收敛重设计方案-20260922》§四 F-3（每个节点一条折叠行，形态同工具卡：
 *   **开始 ／ 判死 或 交付**；「第 N 次尝试」只在异常路径出现）／§二（节点＝事实源；链不持状态 ⇒
 *   零中间层，本行就是面）／§六（只有节点级、只有三个时刻）／§4.9・§4.10（节点渲染件吃 `node`）／
 *   §2.4（组件三戒：不解析载荷、不发请求、不横向读他组件状态）／§9.1（L2 令牌）。
 *
 * state 形制（102 装配层 `assembly/definitions/node-delivery.js` 产出）：
 *   `{ chainId, node, status, moments, attempts }`
 *   · `status` 值域＝`NODE_DELIVERY_STATUS` 四态；文案单一事实源＝`NODE_DELIVERY_STATUS_TEXT`
 *     （102 里组件**不另写文案表**；新树无装配层 ⇒ 本件把该表落成导出常量，逐字取自 102 定义件）
 *   · `moments[]`＝`{ type, at, action?, errorCode?, detail?, causes? }`（时刻**已由定义层解析**，
 *     本件零 JSON 解析；`causes` 项＝`{ node, block, code, rootCause, evidence, knownFixed, needsUser }`）
 *   · `attempts`＝`running` 时刻条数 ⇒ 异常路径（重跑）头部显示「第 N 次尝试」
 *
 * 折叠纪律（同工具卡范式）：折叠头＝**真 button** ＋ `aria-expanded` 双向；展开区就地（不弹层不 teleport）；
 *   折叠不夺焦点；**只读**（不造动作按钮）。
 * 禁做（照 102）：不发请求、不写 store、零定时器、零本地时钟（时刻只呈现定义层给的 `at` 原值）。
 * 上报提级（★P1 20260925「上报未见任何上报卡片提示」）：最近一次 `chain_report` 的 `detail` 直呈折叠头。
 * 🔴 注释纪律（照 102 教训）：文本型注释里禁出现块注释起始符字面（会让剥注释的判据整段吃掉其后行）。
 */
import React, { useRef, useState } from "react";
import type { FlowNodeProps } from "./index";

export interface NodeDeliveryCause {
  node?: string;
  block?: string;
  code?: string;
  rootCause?: string;
  evidence?: string;
  knownFixed?: boolean;
  needsUser?: boolean;
}
export interface NodeDeliveryMoment {
  type?: string;
  at?: string | number;
  action?: string;
  errorCode?: string;
  detail?: string;
  causes?: NodeDeliveryCause[];
}
export interface NodeDeliveryState {
  chainId?: string;
  node?: string;
  status?: string;
  moments?: NodeDeliveryMoment[];
  attempts?: number;
}

/** 状态值域（四态；禁散写字面量）——逐字取自 102 `assembly/definitions/node-delivery.js` */
export const NODE_DELIVERY_STATUS: Readonly<Record<string, string>> = Object.freeze({
  RUNNING: "running", // 进行中
  DELIVERED: "delivered", // 已交付（`done`）
  DEAD: "dead", // 已判死（`failed`）
  CANCELLED: "cancelled", // ★已取消（用户停链；与「已判死」分列）
  BREAK: "break", // 已中断，等待处置（`chain_interrupted`／`chain_paused`）
});
/** 状态文案（**单一事实源**；键集＝状态值域，零新造值） */
export const NODE_DELIVERY_STATUS_TEXT: Readonly<Record<string, string>> = Object.freeze({
  [NODE_DELIVERY_STATUS.RUNNING]: "进行中",
  [NODE_DELIVERY_STATUS.DELIVERED]: "已交付",
  [NODE_DELIVERY_STATUS.DEAD]: "已判死",
  [NODE_DELIVERY_STATUS.CANCELLED]: "已取消",
  [NODE_DELIVERY_STATUS.BREAK]: "已中断（等待处置）",
});
/** 时刻动作文案（**单一事实源**；`advance` 的回边文案由 `action` 决定） */
export const NODE_DELIVERY_MOMENT_TEXT: Readonly<Record<string, string>> = Object.freeze({
  running: "开始",
  done: "交付",
  failed: "判死",
  superseded: "被顶替",
  chain_interrupted: "中断",
  chain_paused: "中断",
  advance: "回边",
  cancelled: "取消", // ★20260925：用户停链
  chain_report: "已上报", // ★20260925：上报合并进本行（不新开卡片）
  node_advance: "当前节点", // ★20260925：模型侧已注入的事实，人面同步可见
});
/** 回边动作文案（值域＝后端 RouteStatus 大写码值；未命中 ⇒ **原样呈现后端值**，不翻译不掩盖） */
export const NODE_DELIVERY_ACTION_TEXT: Readonly<Record<string, string>> = Object.freeze({
  REDO: "重做(redo)",
  TRACEBACK: "打回(traceback)",
  ESCALATE: "上报(escalate)",
  JUMP_BACK: "跳回(jump_back)",
});

/** 时刻文案（逐字＝102 `momentLabelOf`：`advance`＋action ⇒ 动作文案；否则时刻表；都没有 ⇒ 原样/空） */
export function momentLabelOf(moment: NodeDeliveryMoment | null | undefined): string {
  const type = moment?.type ?? null;
  if (type === "advance" && moment?.action !== undefined) {
    return NODE_DELIVERY_ACTION_TEXT[String(moment.action)] ?? String(moment.action);
  }
  return NODE_DELIVERY_MOMENT_TEXT[String(type)] ?? (type === null ? "" : String(type));
}

/**
 * 时刻 → `MM-DD HH:MM`（`at` 原值：数值＝epoch ms，串＝ISO；取不到 ⇒ 空串，**不虚构时刻**）。
 * 102 走单源 `utils/sessionTime.js::parseSessionTime`（含 normalizeIso）；新树该单源不在本批工作面
 * ⇒ 本件就地等价实现（数值直取；串先 `Date.parse`，失败再补「空格→T」重试），语义与 102 单源一致。
 */
export function fmtMomentAt(v: string | number | undefined): string {
  if (v === undefined || v === null || v === "") return "";
  let ms = typeof v === "number" ? v : Date.parse(String(v));
  if (Number.isNaN(ms)) ms = Date.parse(String(v).replace(" ", "T"));
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const CSS = `
.xg-node-delivery { min-width: 0; margin: 0; padding: 2px 0; }
.xg-node-delivery__empty { margin: 0; font-size: var(--xg-font-size-caption); color: var(--xg-text-tertiary); }
.xg-node-delivery__head { display: flex; align-items: center; gap: var(--xg-space-xs); width: 100%; min-height: 24px; min-width: 0; margin: 0; padding: 0; border: none; border-radius: var(--xg-radius-chip); background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.xg-node-delivery__head:hover { background: var(--xg-interactive-hover); }
.xg-node-delivery__head:focus-visible { outline: 2px solid var(--xg-border-focus); outline-offset: -2px; }
.xg-node-delivery__caret { flex: none; width: 16px; color: var(--xg-text-secondary); }
.xg-node-delivery__node { flex: none; font-size: var(--xg-font-size-secondary); line-height: 24px; color: var(--xg-text-secondary); }
.xg-node-delivery__status { flex: none; font-size: var(--xg-font-size-secondary); line-height: 24px; font-weight: 600; color: var(--xg-text-primary); }
.xg-node-delivery__report { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--xg-font-size-caption); color: var(--xg-state-warning-fg); }
.xg-node-delivery__head--break .xg-node-delivery__status { color: var(--xg-state-warning-fg); }
.xg-node-delivery__attempt { flex: none; font-size: var(--xg-font-size-caption); color: var(--xg-state-warning-fg); }
.xg-node-delivery__body { max-height: var(--xg-max-h-output); min-height: 0; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; overflow-wrap: anywhere; margin: var(--xg-space-2xs) 0 0; padding: 0 0 0 var(--xg-space-lg); list-style: none; display: flex; flex-direction: column; gap: 2px; }
.xg-node-delivery__moment { display: flex; flex-wrap: wrap; gap: var(--xg-space-xs); font-size: var(--xg-font-size-caption); line-height: 20px; color: var(--xg-text-secondary); }
.xg-node-delivery__moment-at { flex: none; min-width: 5.5em; color: var(--xg-text-tertiary); font-variant-numeric: tabular-nums; }
.xg-node-delivery__moment-label { flex: none; color: var(--xg-text-secondary); }
.xg-node-delivery__moment-detail { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; color: var(--xg-text-secondary); }
.xg-node-delivery__moment-code { flex: none; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--xg-text-tertiary); }
.xg-node-delivery__causes { flex: 1 0 100%; min-width: 0; margin: 2px 0 0; padding: 0 0 0 var(--xg-space-lg); list-style: none; display: flex; flex-direction: column; gap: 2px; }
.xg-node-delivery__cause { display: flex; flex-direction: column; gap: 0; font-size: var(--xg-font-size-caption); line-height: 18px; color: var(--xg-text-secondary); }
.xg-node-delivery__cause-head { color: var(--xg-text-primary); }
.xg-node-delivery__cause-root { color: var(--xg-text-secondary); overflow-wrap: anywhere; }
.xg-node-delivery__cause-ev { color: var(--xg-text-tertiary); overflow-wrap: anywhere; }
.xg-node-delivery__cause-fixed { color: var(--xg-text-tertiary); }
`;
import { cssIds } from "../../styles/cssIds";
if (typeof document !== "undefined" && !document.getElementById(cssIds.nodeDeliveryRow)) {
  const el = document.createElement("style");
  el.id = cssIds.nodeDeliveryRow;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** 节点交付行（默认折叠；只读；上报提级折叠头） */
export function NodeDeliveryRow({ node }: FlowNodeProps): React.ReactElement {
  const raw = node?.state;
  const s = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as NodeDeliveryState;
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLButtonElement | null>(null);

  const moments = Array.isArray(s.moments) ? s.moments : [];
  /** 节点名（定义层已归一名；缺 ⇒ 空串，不编名） */
  const nodeName = typeof s.node === "string" && s.node !== "" ? s.node : "";
  /** 状态文案：表命中 ⇒ 中文；值域外 ⇒ **原样呈现后端值**（不翻译、不掩盖） */
  const statusText = typeof s.status !== "string" || s.status === ""
    ? "未知"
    : NODE_DELIVERY_STATUS_TEXT[s.status] ?? s.status;
  /** 异常路径标记：重进入 >1 次才出现 */
  const attemptText = Number.isInteger(s.attempts) && Number(s.attempts) > 1
    ? `第 ${s.attempts} 次尝试`
    : "";
  const isBreak = s.status === NODE_DELIVERY_STATUS.BREAK;

  /** 最近一次上报（`chain_report`）——有则折叠头直呈「已上报待处置 ＋ 原因摘要」 */
  const reportMoments = moments.filter((m) => m && m.type === "chain_report");
  const reportMoment = reportMoments.length > 0 ? reportMoments[reportMoments.length - 1] : null;
  const reportReason = (() => {
    const d = reportMoment?.detail;
    if (typeof d !== "string" || d === "") return "";
    return d.length > 60 ? `${d.slice(0, 59)}…` : d;
  })();

  /** 折叠不夺焦点：收起方向焦点在体内 ⇒ 保持展开；否则先把焦点移到折叠头 */
  const toggle = (): void => {
    if (expanded) {
      const ae = typeof document !== "undefined" ? document.activeElement : null;
      const root = rootRef.current;
      const head = headRef.current;
      const inside = !!ae && ae !== document.body && !!root && root !== ae && root.contains(ae)
        && !(head && head.contains(ae));
      if (inside) return;
      if (head && typeof head.focus === "function") head.focus();
    }
    setExpanded((v) => !v);
  };

  return (
    <div
      ref={rootRef}
      className="xg-node-delivery"
      role="group"
      aria-expanded={expanded ? "true" : "false"}
      data-testid="node-delivery"
      data-chat-anchor-key={node?.key}
    >
      {moments.length === 0 ? (
        <p className="xg-node-delivery__empty">节点交付（暂无可用读数）</p>
      ) : (
        <>
          <button
            ref={headRef}
            type="button"
            className={`xg-node-delivery__head xg-touch-44${isBreak ? " xg-node-delivery__head--break" : ""}`}
            aria-expanded={expanded ? "true" : "false"}
            onClick={toggle}
          >
            <span className="xg-node-delivery__caret" aria-hidden="true">{expanded ? "▾" : "▸"}</span>
            <span className="xg-node-delivery__node">{nodeName}</span>
            <span className="xg-node-delivery__status">{statusText}</span>
            {attemptText ? <span className="xg-node-delivery__attempt">{attemptText}</span> : null}
            {reportMoment ? (
              <span
                className="xg-node-delivery__report"
                data-testid="node-delivery-report"
                title={reportMoment.detail || ""}
              >
                {`⚠ 已上报待处置${reportReason ? `：${reportReason}` : ""}`}
              </span>
            ) : null}
          </button>

          {expanded ? (
            <ul className="xg-node-delivery__body">
              {moments.map((m, i) => (
                <li className="xg-node-delivery__moment" key={`${i}:${m?.type ?? ""}:${m?.at ?? ""}`}>
                  <span className="xg-node-delivery__moment-at">{fmtMomentAt(m?.at)}</span>
                  <span className="xg-node-delivery__moment-label">{momentLabelOf(m)}</span>
                  {m?.detail ? (
                    <span className="xg-node-delivery__moment-detail">{m.detail}</span>
                  ) : null}
                  {m?.errorCode ? (
                    <span className="xg-node-delivery__moment-code">{m.errorCode}</span>
                  ) : null}
                  {Array.isArray(m?.causes) && (m?.causes ?? []).length > 0 ? (
                    <ul className="xg-node-delivery__causes">
                      {(m?.causes ?? []).map((c, ci) => (
                        <li className="xg-node-delivery__cause" key={ci}>
                          <span className="xg-node-delivery__cause-head">
                            {`[${ci + 1}] ${c?.node || "—"}·${c?.block || "—"} · ${c?.code || "UNKNOWN"}`}
                          </span>
                          <span className="xg-node-delivery__cause-root">
                            {`这次根因：${c?.rootCause || "—"}`}
                          </span>
                          <span className="xg-node-delivery__cause-ev">
                            {`依据：${c?.evidence || "（无）"}`}
                          </span>
                          <span className="xg-node-delivery__cause-fixed">
                            {`该类是否已修：${c?.knownFixed ? "已修（故障类，有判据）" : "未修 / 待人工处置"}${c?.needsUser ? "；需要用户：是" : ""}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}
